/**
 * A7 · Guided vocal-range test: glides down from a comfortable start, then up,
 * tracking the lowest and highest notes held with confident pitch. Feeds
 * auto-transposition (B2) and the range chart (E3).
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { midiToName, nameToMidi } from '@solfo/shared';
import { api } from '../../lib/api.ts';
import { useAuth } from '../../lib/auth.tsx';
import { t } from '../../lib/i18n.ts';
import { Button, Card, ErrorText } from '../../lib/ui.tsx';
import { MicSession } from '../../audio/mic.ts';
import { playTone } from '../../audio/synth.ts';

type Stage = 'intro' | 'low' | 'between' | 'high' | 'saving' | 'done';

const START_MIDI = nameToMidi('C4');
const HOLD_FRAMES = 12; // frames a note must hold to count as "reliable"

export default function RangeTestPage() {
  const { me, refresh } = useAuth();
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>('intro');
  const [liveNote, setLiveNote] = useState<string | null>(null);
  const [lowMidi, setLowMidi] = useState<number | null>(null);
  const [highMidi, setHighMidi] = useState<number | null>(null);
  const [error, setError] = useState<unknown>(null);
  const micRef = useRef<MicSession | null>(null);
  const holdRef = useRef<{ midi: number; frames: number }>({ midi: 0, frames: 0 });
  const extremeRef = useRef<number | null>(null);

  useEffect(() => () => micRef.current?.close(), []);

  async function runStage(direction: 'low' | 'high') {
    setError(null);
    try {
      const mic = new MicSession();
      await mic.open();
      micRef.current = mic;
      extremeRef.current = null;
      holdRef.current = { midi: 0, frames: 0 };
      playTone(START_MIDI, 1.0);
      setStage(direction);

      mic.startTracking(
        () => 1, // clock irrelevant here
        (p) => {
          const rounded = Math.round(p.midi);
          setLiveNote(midiToName(rounded));
          if (Math.abs(p.midi - holdRef.current.midi) < 0.7) {
            holdRef.current.frames++;
          } else {
            holdRef.current = { midi: rounded, frames: 1 };
          }
          if (holdRef.current.frames >= HOLD_FRAMES) {
            const held = holdRef.current.midi;
            if (extremeRef.current === null) extremeRef.current = held;
            else if (direction === 'low') extremeRef.current = Math.min(extremeRef.current, held);
            else extremeRef.current = Math.max(extremeRef.current, held);
            if (direction === 'low') setLowMidi(extremeRef.current);
            else setHighMidi(extremeRef.current);
          }
        },
      );
    } catch (err) {
      setError(err);
    }
  }

  function endStage(direction: 'low' | 'high') {
    micRef.current?.close();
    micRef.current = null;
    setLiveNote(null);
    setStage(direction === 'low' ? 'between' : 'saving');
    if (direction === 'high') void save();
  }

  async function save() {
    try {
      if (lowMidi === null || highMidi === null || highMidi <= lowMidi) {
        throw new Error('We could not detect a full range — try again in a quieter spot.');
      }
      await api(`/api/students/${me!.id}/range-test`, { body: { lowMidi, highMidi } });
      await refresh();
      setStage('done');
    } catch (err) {
      setError(err);
      setStage('between');
    }
  }

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-bold">{t('rangetest.title', 'Vocal range test')}</h1>

      {stage === 'intro' && (
        <Card>
          <p className="text-sm text-slate-600">
            {t('rangetest.intro', 'You will hear a reference tone (C4). Glide gently DOWN as low as you comfortably can, hold your lowest good note, then we do the same going UP.')}
          </p>
          <p className="mt-2 text-xs text-slate-400">No pushing, no straining — “reliable” beats “barely reachable”.</p>
          <Button className="mt-4 w-full" onClick={() => runStage('low')}>
            🎹 Play the tone & glide down
          </Button>
        </Card>
      )}

      {(stage === 'low' || stage === 'high') && (
        <Card className="text-center">
          <p className="text-xs tracking-wide text-slate-400 uppercase">
            {stage === 'low' ? 'Gliding down…' : 'Gliding up…'}
          </p>
          <p className="my-4 text-5xl font-bold text-brand-700">{liveNote ?? '—'}</p>
          <p className="text-sm text-slate-500">
            {stage === 'low'
              ? `Lowest held so far: ${lowMidi !== null ? midiToName(lowMidi) : '—'}`
              : `Highest held so far: ${highMidi !== null ? midiToName(highMidi) : '—'}`}
          </p>
          <Button className="mt-4 w-full" onClick={() => endStage(stage)}>
            That's my {stage === 'low' ? 'lowest' : 'highest'}
          </Button>
        </Card>
      )}

      {stage === 'between' && (
        <Card className="text-center">
          <p className="text-sm text-slate-600">
            Low note locked in: <strong>{lowMidi !== null ? midiToName(lowMidi) : '—'}</strong>
          </p>
          <p className="mt-2 text-sm text-slate-600">Now the fun part — glide up. 🎈</p>
          <Button className="mt-4 w-full" onClick={() => runStage('high')}>
            🎹 Play the tone & glide up
          </Button>
          <ErrorText error={error} />
        </Card>
      )}

      {stage === 'saving' && <Card className="text-center text-sm text-slate-500">Saving your range…</Card>}

      {stage === 'done' && (
        <Card className="text-center">
          <p className="text-sm text-slate-500">Your reliable range</p>
          <p className="my-3 text-3xl font-bold text-brand-700">
            {lowMidi !== null && midiToName(lowMidi)} – {highMidi !== null && midiToName(highMidi)}
          </p>
          <p className="text-xs text-slate-500">
            Every exercise will now be transposed to fit your voice automatically. Your coach can fine-tune it.
          </p>
          <Button className="mt-4 w-full" onClick={() => navigate('/app')}>
            To today's practice →
          </Button>
        </Card>
      )}

      <ErrorText error={stage === 'intro' || stage === 'low' || stage === 'high' ? error : null} />
    </div>
  );
}
